import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { Confetti } from '../lib/animations';
import { GameHeader, ScoreBadge, PremiumCardBack, FloatingParticles, BeaverCharacter } from '../lib/GameVisuals';
import { Grid2x2, CheckCircle, XCircle, Zap, ChevronRight, Layers } from 'lucide-react';

/* ─── Match type display labels ─── */
const MATCH_TYPE_INFO = {
  french_english: { label: 'French ↔ English', color: '#4C6EF5', tagA: 'FR', tagB: 'EN' },
  infinitive_conjugated: { label: 'Infinitif ↔ Conjugué', color: '#E03131', tagA: 'Inf', tagB: 'Conj' },
  infinitive_past_participle: { label: 'Infinitif ↔ Participe', color: '#7950F2', tagA: 'Inf', tagB: 'P.P.' },
  french_french: { label: 'Français ↔ Français', color: '#0CA678', tagA: 'A', tagB: 'B' },
  masculine_feminine: { label: 'Masculin ↔ Féminin', color: '#F06595', tagA: 'M', tagB: 'F' },
  question_answer: { label: 'Question ↔ Réponse', color: '#F59F00', tagA: 'Q', tagB: 'R' },
  antonym_pairs: { label: 'Contraires', color: '#E03131', tagA: 'A', tagB: 'B' },
  antonym: { label: 'Contraires', color: '#E03131', tagA: 'A', tagB: 'B' },
  verb_conjugation: { label: 'Conjugaison', color: '#E03131', tagA: 'Inf', tagB: 'Conj' },
  subject_agreement: { label: 'Sujet ↔ Accord', color: '#7950F2', tagA: 'Suj', tagB: 'Acc' },
  sentence_halves: { label: 'Début ↔ Fin', color: '#0CA678', tagA: '1', tagB: '2' },
  pronoun_verb_form: { label: 'Pronom ↔ Verbe', color: '#4C6EF5', tagA: 'Pro', tagB: 'V' },
  pronoun_avoir: { label: 'Pronom ↔ Avoir', color: '#4C6EF5', tagA: 'Pro', tagB: 'Avoir' },
  profession_workplace: { label: 'Profession ↔ Lieu', color: '#F59F00', tagA: 'Prof', tagB: 'Lieu' },
  present_future: { label: 'Présent ↔ Futur', color: '#0CA678', tagA: 'Prés', tagB: 'Fut' },
  auxiliary_verb: { label: 'Auxiliaire ↔ Verbe', color: '#7950F2', tagA: 'Aux', tagB: 'V' },
  affirmative_negative: { label: 'Affirmatif ↔ Négatif', color: '#E03131', tagA: '+', tagB: '–' },
};

function getMatchInfo(matchType) {
  return MATCH_TYPE_INFO[matchType] || { label: matchType || 'Matching', color: '#4C6EF5', tagA: 'A', tagB: 'B' };
}

/* ─── Compute optimal grid layout from pair count ─── */
function computeGrid(pairCount) {
  const totalCards = pairCount * 2;
  // Collect all exact-fit grids where cols >= rows (landscape-friendly)
  const layouts = [];
  for (let rows = 2; rows <= 5; rows++) {
    if (totalCards % rows === 0) {
      const cols = totalCards / rows;
      if (cols >= rows && cols <= 8) layouts.push({ rows, cols });
    }
  }
  // Prefer most balanced (smallest difference between cols and rows)
  if (layouts.length > 0) {
    layouts.sort((a, b) => (a.cols - a.rows) - (b.cols - b.rows));
    return layouts[0];
  }
  // No exact landscape fit — try any exact fit
  for (let rows = 2; rows <= 5; rows++) {
    if (totalCards % rows === 0) {
      const cols = totalCards / rows;
      if (cols >= 2 && cols <= 8) return { rows, cols };
    }
  }
  // Fallback: find nearest grid that fits
  for (let rows = 2; rows <= 5; rows++) {
    for (let cols = rows; cols <= 8; cols++) {
      if (rows * cols >= totalCards) return { rows, cols };
    }
  }
  return { rows: 4, cols: 4 };
}

/* ─── Prepare rounds from items ─── */
function prepareRounds(items, maxRounds) {
  const memoireItems = items.filter(i => i.pairs && i.pairs.length > 0);
  if (memoireItems.length === 0) return [];

  // Each item = one round. Limit to maxRounds.
  const selected = memoireItems.slice(0, maxRounds === 999 ? memoireItems.length : maxRounds);

  return selected.map(item => {
    // Deduplicate pairs
    const seen = new Set();
    let pairs = (item.pairs || []).filter(p => {
      const key = `${p.card_a}||${p.card_b}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Compute grid from pair count, trim if no exact fit
    let grid = computeGrid(pairs.length);
    const maxPairs = Math.floor((grid.rows * grid.cols) / 2);
    if (pairs.length > maxPairs) pairs = pairs.slice(0, maxPairs);
    // If grid has extra slots, reduce grid or trim pairs
    const totalSlots = grid.rows * grid.cols;
    if (pairs.length * 2 < totalSlots) {
      // Recompute with actual pair count
      grid = computeGrid(pairs.length);
    }

    return {
      title: item.title || 'Memory Match',
      matchType: item.match_type || 'french_english',
      pairs,
      grid,
      difficulty: item.difficulty,
      curriculumTags: item.curriculum_tags,
    };
  });
}

/* ─── Tutorial Screen ─── */
function TutorialScreen({ onStart, items }) {
  const matchTypes = useMemo(() => {
    const types = new Set();
    items.forEach(i => { if (i.match_type) types.add(i.match_type); });
    return [...types].map(t => getMatchInfo(t));
  }, [items]);

  const firstItem = items[0];
  const samplePair = firstItem?.pairs?.[0];

  return (
    <div data-testid="memoire-tutorial" style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #E7F5FF 0%, #F0F4F8 40%, #FFF9DB 100%)',
      padding: '2rem',
    }}>
      <div style={{ maxWidth: 800, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <Grid2x2 size={48} color="#4C6EF5" />
          <h1 style={{ fontFamily: 'Fredoka', fontSize: '3.2rem', fontWeight: 700, color: '#1A1A2E' }}>
            LA MÉMOIRE
          </h1>
        </div>
        <p style={{ fontFamily: 'Nunito', fontSize: '1.5rem', color: '#333', marginBottom: '2rem', fontWeight: 600 }}>
          Flip cards to find matching pairs!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {/* Card 1: How to play */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(76,110,245,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Grid2x2 size={40} color="#4C6EF5" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#4C6EF5', marginBottom: '0.3rem' }}>
              Flip
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Tap any card to flip it and see the word underneath
            </div>
          </div>

          {/* Card 2: Match */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(47,158,68,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <CheckCircle size={40} color="#2F9E44" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#2F9E44', marginBottom: '0.3rem' }}>
              Match
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Find two cards that belong together — they stay flipped!
            </div>
          </div>

          {/* Card 3: Streak */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(245,159,0,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Zap size={40} color="#F59F00" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#F59F00', marginBottom: '0.3rem' }}>
              Streak!
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Match pairs in a row without mistakes for bonus points
            </div>
          </div>
        </div>

        {/* Show match type examples */}
        {samplePair && (
          <div style={{
            background: 'rgba(255,255,255,0.85)', borderRadius: '1rem', padding: '1rem 1.5rem',
            border: '2px solid rgba(76,110,245,0.15)', marginBottom: '1.5rem',
            display: 'inline-flex', alignItems: 'center', gap: '1rem',
          }}>
            <span style={{ fontFamily: 'Nunito', fontSize: '1.1rem', color: '#555', fontWeight: 600 }}>Example:</span>
            <span style={{
              fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 700, color: '#4C6EF5',
              background: '#4C6EF510', padding: '0.3rem 0.8rem', borderRadius: '0.5rem',
            }}>{samplePair.card_a}</span>
            <span style={{ fontFamily: 'Fredoka', fontSize: '1.4rem', color: '#999' }}>=</span>
            <span style={{
              fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 700, color: '#2F9E44',
              background: '#2F9E4410', padding: '0.3rem 0.8rem', borderRadius: '0.5rem',
            }}>{samplePair.card_b}</span>
          </div>
        )}

        {/* Match types in this session */}
        {matchTypes.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ fontFamily: 'Nunito', fontSize: '0.9rem', color: '#888', marginBottom: '0.5rem', fontWeight: 600 }}>
              Match types in this session:
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {matchTypes.map((mt, i) => (
                <span key={i} style={{
                  fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 600, color: mt.color,
                  background: `${mt.color}12`, padding: '0.3rem 0.75rem', borderRadius: '0.5rem',
                  border: `1.5px solid ${mt.color}30`,
                }}>{mt.label}</span>
              ))}
            </div>
          </div>
        )}

        <button data-testid="memoire-start-button" onClick={onStart} style={{
          fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700,
          padding: '0.75rem 3rem', borderRadius: '1rem',
          background: '#4C6EF5', color: 'white', border: 'none',
          borderBottom: '6px solid #3B5BDB',
          cursor: 'pointer', boxShadow: '0 4px 16px rgba(76,110,245,0.3)',
          display: 'inline-flex', alignItems: 'center', gap: '0.75rem',
        }}>
          Let's Play! <ChevronRight size={32} />
        </button>
      </div>
    </div>
  );
}

/* ─── Set Transition Screen ─── */
function SetTransition({ round, totalRounds, title, matchType, onReady }) {
  const info = getMatchInfo(matchType);
  const transRef = useRef(null);

  useEffect(() => {
    if (transRef.current) {
      gsap.fromTo(transRef.current.children,
        { y: 30, opacity: 0, scale: 0.9 },
        { y: 0, opacity: 1, scale: 1, duration: 0.4, stagger: 0.1, ease: 'back.out(1.7)' }
      );
    }
  }, [round]);

  return (
    <div data-testid="memoire-set-transition" style={{
      position: 'fixed', inset: 0, zIndex: 90,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #E7F5FF 0%, #F8F9FA 100%)',
      padding: '2rem',
    }}>
      <div ref={transRef} style={{ textAlign: 'center', maxWidth: 600 }}>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 600, color: '#868E96',
          letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem',
        }}>
          SET {round} OF {totalRounds}
        </div>
        <h2 style={{
          fontFamily: 'Fredoka', fontSize: '2.5rem', fontWeight: 700, color: '#1A1A2E',
          marginBottom: '0.75rem', lineHeight: 1.2,
        }}>
          {title}
        </h2>
        <div style={{
          display: 'inline-block', fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 700,
          color: info.color, background: `${info.color}12`, padding: '0.4rem 1rem',
          borderRadius: '0.5rem', border: `2px solid ${info.color}30`, marginBottom: '2rem',
        }}>
          {info.label}
        </div>
        <div>
          <button data-testid="memoire-ready-button" onClick={onReady} style={{
            fontFamily: 'Fredoka', fontSize: '1.75rem', fontWeight: 700,
            padding: '0.6rem 2.5rem', borderRadius: '1rem',
            background: '#4C6EF5', color: 'white', border: 'none',
            borderBottom: '5px solid #3B5BDB',
            cursor: 'pointer', boxShadow: '0 4px 16px rgba(76,110,245,0.3)',
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          }}>
            Ready! <ChevronRight size={28} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Game ─── */
export function LaMemoire({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('memoire'); return () => stopMusic(); }, [playGameMusic, stopMusic]);

  // Prepare rounds from items
  const rounds = useMemo(() => prepareRounds(items, settings.rounds || 10), [items, settings.rounds]);

  const [phase, setPhase] = useState('tutorial'); // tutorial | transition | playing
  const [currentRound, setCurrentRound] = useState(0);

  // Per-round state
  const [cards, setCards] = useState([]);
  const [gridLayout, setGridLayout] = useState({ rows: 4, cols: 4 });
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState(new Set());
  const [canFlip, setCanFlip] = useState(false);

  // Peek mode state
  const [peeking, setPeeking] = useState(false);
  const [peekCountdown, setPeekCountdown] = useState(0); // 3,2,1,0
  const peekTimerRef = useRef(null);

  // Global stats
  const [totalPairsFound, setTotalPairsFound] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [roundsCompleted, setRoundsCompleted] = useState(0);
  const [showConfetti, setShowConfetti] = useState(0);

  const scoreRef = useRef(null);
  const streakRef = useRef(null);
  const cardRefs = useRef({});
  const gameEndedRef = useRef(false);
  const matchInfoRef = useRef(null);
  const reviewSetsRef = useRef([]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu.
  // Submits whatever pairs/stats the student has so far.
  useEffect(() => {
    const onForceEnd = () => {
      if (gameEndedRef.current) return;
      gameEndedRef.current = true;
      const review = rounds.map((r) => ({
        setTitle: r.title, matchType: getMatchInfo(r.matchType).label,
        pairs: r.pairs.map(p => ({ a: p.card_a, b: p.card_b })),
      }));
      setTimeout(() => {
        onGameEnd({
          team1Score: totalPairsFound, team2Score: 0,
          totalQuestions: totalAttempts,
          accuracy: totalAttempts > 0 ? (totalPairsFound / totalAttempts) * 100 : 0,
          bestStreak,
          questionReview: review, reviewType: 'pairs',
        });
      }, 600);
    };
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [rounds, totalPairsFound, totalAttempts, bestStreak, onGameEnd]);

  // Initialize a round's cards
  const initRound = useCallback((roundIndex) => {
    if (roundIndex >= rounds.length) {
      // All rounds done — build review from all rounds
      if (!gameEndedRef.current) {
        gameEndedRef.current = true;
        const review = rounds.map((r, i) => ({
          setTitle: r.title, matchType: getMatchInfo(r.matchType).label,
          pairs: r.pairs.map(p => ({ a: p.card_a, b: p.card_b })),
        }));
        setTimeout(() => {
          onGameEnd({
            team1Score: totalPairsFound, team2Score: 0,
            totalQuestions: totalAttempts,
            accuracy: totalAttempts > 0 ? (totalPairsFound / totalAttempts) * 100 : 0,
            bestStreak,
            questionReview: review, reviewType: 'pairs',
          });
        }, 800);
      }
      return;
    }

    const round = rounds[roundIndex];
    const { pairs, grid } = round;

    // Create card array: each pair creates 2 cards (a and b)
    const arr = [];
    pairs.forEach((p, i) => {
      arr.push({ id: i * 2, pairId: i, text: p.card_a, side: 'a' });
      arr.push({ id: i * 2 + 1, pairId: i, text: p.card_b, side: 'b' });
    });

    // Fisher-Yates shuffle
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }

    setCards(arr);
    setGridLayout(grid);
    setFlipped([]);
    setMatched(new Set());
    setCanFlip(false); // disabled during peek
    setPeeking(true);
    setPeekCountdown(3);
    cardRefs.current = {};
  }, [rounds, totalPairsFound, totalAttempts, bestStreak, onGameEnd]);

  // Peek countdown timer
  useEffect(() => {
    if (!peeking || phase !== 'playing') return;
    if (peekCountdown > 0) {
      peekTimerRef.current = setTimeout(() => {
        setPeekCountdown(c => c - 1);
      }, 1000);
      return () => clearTimeout(peekTimerRef.current);
    }
    // Countdown reached 0 — flip all cards back
    const els = Object.values(cardRefs.current);
    if (els.length > 0) {
      // Flip-back animation: all cards scale to 0 then back
      gsap.to(els, {
        scaleX: 0, duration: 0.15, stagger: 0.01,
        onComplete: () => {
          setPeeking(false);
          gsap.to(els, { scaleX: 1, duration: 0.15, stagger: 0.01, ease: 'back.out(1.5)', onComplete: () => setCanFlip(true) });
        },
      });
    } else {
      setPeeking(false);
      setCanFlip(true);
    }
  }, [peeking, peekCountdown, phase]);

  // Handle card click
  const handleCardClick = useCallback((idx) => {
    if (!canFlip || flipped.includes(idx) || matched.has(cards[idx]?.pairId)) return;
    playSound('flip');

    const el = cardRefs.current[idx];
    if (el) gsap.fromTo(el, { scaleX: 0 }, { scaleX: 1, duration: 0.2, ease: 'back.out(1.5)' });

    if (flipped.length === 0) {
      setFlipped([idx]);
    } else if (flipped.length === 1) {
      const first = flipped[0];
      setFlipped([first, idx]);
      setCanFlip(false);
      setTotalAttempts(a => a + 1);

      if (cards[first].pairId === cards[idx].pairId && first !== idx) {
        // Match found!
        playSound('match');
        setTimeout(() => {
          [first, idx].forEach(i => {
            const e = cardRefs.current[i];
            if (e) gsap.to(e, { scale: 1.1, duration: 0.2, yoyo: true, repeat: 1, ease: 'power2.out' });
          });
          setMatched(prev => new Set([...prev, cards[first].pairId]));
          setTotalPairsFound(s => s + 1);
          setCurrentStreak(s => {
            const next = s + 1;
            setBestStreak(b => Math.max(b, next));
            return next;
          });
          if (scoreRef.current) gsap.fromTo(scoreRef.current, { scale: 1.4 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
          if (streakRef.current) gsap.fromTo(streakRef.current, { scale: 1.3 }, { scale: 1, duration: 0.3, ease: 'back.out(2)' });
          setFlipped([]);
          setCanFlip(true);
        }, 500);
      } else {
        // No match
        playSound('wrong');
        setCurrentStreak(0);
        setTimeout(() => {
          [first, idx].forEach(i => {
            const e = cardRefs.current[i];
            if (e) {
              gsap.to(e, {
                x: 6, duration: 0.06, repeat: 4, yoyo: true,
                onComplete: () => {
                  gsap.fromTo(e, { scaleX: 1 }, {
                    scaleX: 0, duration: 0.12,
                    onComplete: () => gsap.fromTo(e, { scaleX: 0 }, { scaleX: 1, duration: 0.12 }),
                  });
                },
              });
            }
          });
          setFlipped([]);
          setCanFlip(true);
        }, 900);
      }
    }
  }, [flipped, matched, canFlip, cards, playSound]);

  // Check round completion
  const pairsNeeded = useMemo(() => {
    if (currentRound >= rounds.length) return 0;
    return rounds[currentRound]?.pairs?.length || 0;
  }, [currentRound, rounds]);

  useEffect(() => {
    if (phase !== 'playing' || pairsNeeded === 0) return;
    if (matched.size >= pairsNeeded) {
      setShowConfetti(c => c + 1);
      playSound('win');
      setRoundsCompleted(r => r + 1);

      const nextRound = currentRound + 1;
      if (nextRound >= rounds.length) {
        // All rounds done — build review
        if (!gameEndedRef.current) {
          gameEndedRef.current = true;
          const review = rounds.map((r) => ({
            setTitle: r.title, matchType: getMatchInfo(r.matchType).label,
            pairs: r.pairs.map(p => ({ a: p.card_a, b: p.card_b })),
          }));
          setTimeout(() => {
            onGameEnd({
              team1Score: totalPairsFound + pairsNeeded, // include current round
              team2Score: 0,
              totalQuestions: totalAttempts,
              accuracy: totalAttempts > 0 ? ((totalPairsFound + pairsNeeded) / totalAttempts) * 100 : 0,
              bestStreak,
              questionReview: review, reviewType: 'pairs',
            });
          }, 1500);
        }
      } else {
        // Move to next round after a delay
        setTimeout(() => {
          setCurrentRound(nextRound);
          setPhase('transition');
        }, 1200);
      }
    }
  }, [matched.size, pairsNeeded, phase]);

  // Animate cards entrance
  useEffect(() => {
    if (phase !== 'playing') return;
    const els = Object.values(cardRefs.current);
    if (els.length > 0) {
      gsap.fromTo(els,
        { scale: 0.7, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.3, stagger: 0.02, ease: 'back.out(1.7)' }
      );
    }
  }, [cards, phase]);

  // Tutorial → first transition
  const handleTutorialDone = () => {
    setPhase('transition');
    setCurrentRound(0);
  };

  // Transition → playing
  const handleTransitionReady = () => {
    initRound(currentRound);
    setPhase('playing');
  };

  // Current round info
  const roundInfo = rounds[currentRound] || {};
  const matchInfo = getMatchInfo(roundInfo.matchType);

  if (rounds.length === 0) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.5rem', color: 'var(--text-secondary)' }}>No memory sets available</div>
      </div>
    );
  }

  if (phase === 'tutorial') {
    return <TutorialScreen onStart={handleTutorialDone} items={items} />;
  }

  if (phase === 'transition') {
    return (
      <SetTransition
        round={currentRound + 1}
        totalRounds={rounds.length}
        title={roundInfo.title}
        matchType={roundInfo.matchType}
        onReady={handleTransitionReady}
      />
    );
  }

  // Calculate card text size based on grid and text length
  const getCardFontSize = (text, cols) => {
    const baseSize = cols <= 4 ? 1.6 : cols <= 5 ? 1.4 : 1.2;
    if (text.length > 25) return `${baseSize * 0.65}rem`;
    if (text.length > 15) return `${baseSize * 0.8}rem`;
    if (text.length > 10) return `${baseSize * 0.9}rem`;
    return `${baseSize}rem`;
  };

  return (
    <div data-testid="la-memoire-game" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #E7F5FF 0%, #F0F4F8 40%, #FFF9DB 100%)',
      position: 'relative',
    }}>
      <Confetti trigger={showConfetti} />
      <FloatingParticles type="maple" count={5} />

      {/* Header */}
      <GameHeader title="LA MÉMOIRE" icon={<Grid2x2 size={24} color="var(--primary)" />}>
        <ScoreBadge label="PAIRS" value={`${totalPairsFound + matched.size}/${rounds.reduce((s, r) => s + r.pairs.length, 0)}`} color="var(--primary)" scoreRef={scoreRef} />
        {currentStreak >= 2 && (
          <div ref={streakRef} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Zap size={18} color="#F59F00" />
            <span style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 700, color: '#F59F00' }}>{currentStreak}</span>
          </div>
        )}
        <ScoreBadge label="TRIES" value={totalAttempts} color="var(--text-muted)" />
      </GameHeader>

      {/* Match type badge + round progress bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '1rem', padding: '0.5rem 1rem',
        background: 'rgba(255,255,255,0.7)',
      }}>
        <span ref={matchInfoRef} style={{
          fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700,
          color: matchInfo.color, background: `${matchInfo.color}10`,
          padding: '0.2rem 0.7rem', borderRadius: '0.5rem',
          border: `1.5px solid ${matchInfo.color}25`,
        }}>
          {matchInfo.label}
        </span>
        <span style={{ fontFamily: 'Nunito', fontSize: '0.9rem', color: '#868E96', fontWeight: 600 }}>
          Set {currentRound + 1}/{rounds.length}
        </span>
        {/* Mini progress dots */}
        <div style={{ display: 'flex', gap: '0.3rem' }}>
          {rounds.map((_, i) => (
            <div key={i} style={{
              width: 10, height: 10, borderRadius: '50%',
              background: i < currentRound ? '#2F9E44' : i === currentRound ? '#4C6EF5' : '#DEE2E6',
              transition: 'background 0.3s',
            }} />
          ))}
        </div>
      </div>

      {/* Card grid */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0.5rem 1rem 1rem',
        position: 'relative',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridLayout.cols}, 1fr)`,
          gridTemplateRows: `repeat(${gridLayout.rows}, 1fr)`,
          gap: gridLayout.cols > 5 ? '0.4rem' : '0.6rem',
          maxWidth: 1100,
          width: '100%',
          height: '100%',
          maxHeight: gridLayout.rows <= 3 ? '60vh' : '75vh',
        }}>
          {cards.map((card, idx) => {
            const isFlipped = flipped.includes(idx);
            const isMatched = matched.has(card.pairId);
            const showFace = isFlipped || isMatched || peeking;
            return (
              <div
                key={`${currentRound}-${idx}`}
                data-testid={`memory-card-${idx}`}
                ref={el => { if (el) cardRefs.current[idx] = el; }}
                onClick={() => handleCardClick(idx)}
                style={{
                  borderRadius: '0.75rem',
                  border: isMatched ? '3px solid var(--success)' : '2px solid rgba(0,0,0,0.08)',
                  background: isMatched
                    ? 'linear-gradient(135deg, #E6F9ED 0%, #D3F0DB 100%)'
                    : showFace ? 'white' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: isMatched ? 'default' : 'pointer',
                  boxShadow: isMatched
                    ? '0 0 16px rgba(47,158,68,0.2), 0 2px 8px rgba(0,0,0,0.05)'
                    : '0 4px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)',
                  touchAction: 'manipulation',
                  padding: '0.4rem',
                  overflow: 'hidden',
                  transition: 'border-color 0.3s, box-shadow 0.3s',
                  minHeight: 0,
                  position: 'relative',
                }}
              >
                {showFace ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    {/* Side indicator tag */}
                    <span style={{
                      position: 'absolute', top: 4, left: 6,
                      fontFamily: 'Fredoka', fontSize: '0.65rem', fontWeight: 700,
                      color: card.side === 'a' ? matchInfo.color : '#2F9E44',
                      opacity: 0.6,
                    }}>
                      {card.side === 'a' ? matchInfo.tagA : matchInfo.tagB}
                    </span>
                    <span style={{
                      fontSize: getCardFontSize(card.text, gridLayout.cols),
                      fontWeight: 700,
                      color: isMatched ? 'var(--success)' : 'var(--text-primary)',
                      textAlign: 'center',
                      wordBreak: 'break-word',
                      fontFamily: 'Fredoka, sans-serif',
                      lineHeight: 1.2,
                      padding: '0.2rem',
                    }}>
                      {card.text}
                    </span>
                  </div>
                ) : (
                  <PremiumCardBack variant={idx % 3} />
                )}
              </div>
            );
          })}
        </div>

        {/* Peek countdown overlay */}
        {peeking && (
          <div data-testid="peek-overlay" style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none', zIndex: 30,
          }}>
            <div style={{
              background: 'rgba(0,0,0,0.6)',
              borderRadius: '1.5rem',
              padding: '1.5rem 3rem',
              backdropFilter: 'blur(4px)',
              textAlign: 'center',
              animation: 'peekPulse 1s ease-in-out infinite',
            }}>
              <style>{`@keyframes peekPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }`}</style>
              <div style={{
                fontFamily: 'Fredoka', fontSize: '1.4rem', fontWeight: 700,
                color: '#FFD43B', letterSpacing: '0.15em', textTransform: 'uppercase',
                marginBottom: '0.25rem',
              }}>
                {peekCountdown > 0 ? 'MEMORIZE!' : 'GO!'}
              </div>
              <div style={{
                fontFamily: 'Fredoka', fontSize: '4rem', fontWeight: 800,
                color: 'white', lineHeight: 1,
              }}>
                {peekCountdown > 0 ? peekCountdown : ''}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
